import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useKpiStore } from "../stores/kpiStore";
import { useUiStore } from "../stores/uiStore";

/** レーンヘッダ下の「今日の数字」。手動入力 + MCP/API 書き込みの表示面。 */
export function KpiChips({ projectId }: { projectId: string }) {
  // セレクタ内 filter は毎回新配列 → Zustand v5 で無限再レンダーになるため useMemo で絞る
  const allEntries = useKpiStore((s) => s.entries);
  const entries = useMemo(
    () => allEntries.filter((e) => e.projectId === projectId),
    [allEntries, projectId]
  );
  const upsert = useKpiStore((s) => s.upsert);
  const showToast = useUiStore((s) => s.showToast);
  const [adding, setAdding] = useState(false);
  const [key, setKey] = useState("");
  const [value, setValue] = useState("");

  const submit = async () => {
    if (!key.trim() || !value.trim()) return;
    try {
      await upsert({ projectId, key: key.trim(), value: value.trim() });
      setKey("");
      setValue("");
      setAdding(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "KPIの保存に失敗しました";
      showToast(
        msg.includes("Flow api method not found") ? "この機能はデスクトップ版のみです" : msg
      );
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border px-3 py-1.5 dark:border-border-dark">
      {entries.map((e) => (
        <span
          key={e.id}
          className="rounded-full bg-bg px-2 py-0.5 text-[10px] text-muted dark:bg-bg-dark dark:text-muted-dark"
          title={`更新: ${e.updatedAt}`}
        >
          {e.key} <strong className="text-text dark:text-text-dark">{e.value}</strong>
        </span>
      ))}
      {adding ? (
        <span className="flex items-center gap-1">
          <input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="指標"
            maxLength={64}
            className="h-6 w-16 rounded border border-border bg-bg px-1 text-[10px] dark:border-border-dark dark:bg-bg-dark"
          />
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="値"
            maxLength={256}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) void submit();
            }}
            className="h-6 w-14 rounded border border-border bg-bg px-1 text-[10px] dark:border-border-dark dark:bg-bg-dark"
          />
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!key.trim() || !value.trim()}
            className="rounded bg-brand-500 px-1.5 py-0.5 text-[10px] font-semibold text-white disabled:opacity-50"
          >
            保存
          </button>
        </span>
      ) : (
        <button
          type="button"
          aria-label="今日の数字を追加"
          onClick={() => setAdding(true)}
          className="flex h-5 w-5 items-center justify-center rounded-full text-dim hover:bg-hover dark:hover:bg-hover-dark"
        >
          <Plus className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}
